import * as React from "react";
import {none, option, Option} from "ts-option";
import CurrencyHelper from "../../../helper/CurrencyHelper";
import DataGrid from "../../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import IconWrapper from "../../../hiberUiKit/src/atoms/Icon/IconWrapper";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import InvoiceItem, {InvoiceItemType} from "../../../model/invoice/InvoiceItem";
import IconSmall from "../../../hiberUiKit/src/atoms/Icon/IconSmall";
import Invoice from "../../../model/invoice/Invoice";
import Input from "../../../hiberUiKit/src/atoms/Input/Input";
import RangeNumberInput from "../../../hiberUiKit/src/atoms/Input/RangeNumberInput";
import CurrencyInput from "../../../hiberUiKit/src/atoms/Input/CurrencyInput";
import SelectAccount from "../SelectAccount";
import Account from "../../../model/account/Account";
import AccountNamePresenter from "../AccountNamePresenter";
import SelectTaxRate, {Tax} from "../SelectTaxRate";
import classNames = require("classnames");
import "./invoiceItemsTable.scss";

interface IComponentProps {
	/**
	 * Invoice to display.
	 */
	invoice: Option<Invoice>;
	/**
	 * Invoice edit mode indicator.
	 */
	editMode: boolean;
	/**
	 * Accounts.
	 */
	accounts: Option<Account[]>;
	/**
	 * Vat rate.
	 */
	vat: Option<number>;
	/**
	 * Description for labour and parts item.
	 */
	labourAndPartsItemDescription: Option<string>;
	/**
	 * Labour and parts item description change callback.
	 * @param description
	 */
	onLabourAndPartsItemDescriptionChange: (description: string) => void;
	/**
	 * Labour and parts item edit icon click callback.
	 */
	onLabourAndPartsItemEditClick: () => void;
	/**
	 * Invoice change callback.
	 * @param invoice
	 */
	onChange: (invoice: Invoice) => void;
}

interface IState {
	/**
	 * Edited item id.
	 */
	editedItemId: Option<string>;
	/**
	 * Column being currently edited.
	 */
	editedColumn: Option<InvoiceItemColumn>;
	/**
	 * Unit price temporary field (used for item editing)
	 */
	tempUnitPrice: string;
}

enum InvoiceItemColumn {
	Description = "DESCRIPTION",
	Quantity = "QUANTITY",
	UnitPrice = "UNIT_PRICE",
	Account = "ACCOUNT",
	TaxRate = "TAX_RATE"
}

export default class InvoiceItemsTable extends React.Component<IComponentProps, IState> {
	constructor(props: IComponentProps) {
		super(props);
		this.state = {
			editedItemId: none,
			editedColumn: none,
			tempUnitPrice: ""
		};
	}

	render() {
		return (
			<div className="c-invoice-table">
				<DataGrid>
					<thead>
					<tr className="c-common-details_table-header-row">
						{this.props.editMode &&
						<th style={{width: "97px", textAlign: "end"}}>
							<div className="c-invoice-table_add-item-button">
								<IconWrapper variant={"white"}
											 icon={Icon.Add}
											 onClick={this.addNewItem}
											 size={18}/>
							</div>
						</th>}
						<th className={this.getDescThClassFromProps()}>DESCRIPTION</th>
						<th className="c-invoice-table_qty-cell">QTY</th>
						<th className="c-invoice-table_unit-price-cell">UNIT PRICE</th>
						<th className="c-invoice-table_disc-cell">DISC %</th>
						<th className="c-invoice-table_account-cell">ACCOUNT</th>
						<th className="c-invoice-table_tax-rate-cell">TAX RATE</th>
						<th className="c-invoice-table_amount-cell">AMOUNT</th>
						{this.props.editMode && <th className="c-invoice-table_edit-cell"/>}
					</tr>
					</thead>
					<tbody>
					{this.props.invoice.map(inv => inv.items.map((item, index) => {
							const classFromState = classNames({
								"c-common-details_table-row": true,
								"edit-mode": this.props.editMode
							});
							return <tr key={index} className={classFromState}>
								{this.props.editMode && <td/>}
								<td className={this.getDescTdClassFromProps(item.id)}
									onClick={() => this.setState(
										{
											editedItemId: option(item.id),
											editedColumn: option(InvoiceItemColumn.Description)
										})}>
									{this.renderDescriptionCellContent(item)}
								</td>
								<td className={this.getQtyTdClassFromProps(item)}
									onClick={() => this.setState(
										{
											editedItemId: option(item.id),
											editedColumn: option(InvoiceItemColumn.Quantity)
										})}>
									{this.renderQuantityCellContent(item)}
								</td>
								<td className={this.getUnitPriceTdClassFromProps(item)}
									onClick={() => this.setState(
										{
											editedItemId: option(item.id),
											editedColumn: option(InvoiceItemColumn.UnitPrice),
											tempUnitPrice: item.unitPrice ? item.unitPrice.toFixed(2) : ""
										})}>
									{this.renderUnitPriceCellContent(item)}
								</td>
								<td className="c-invoice-table_disc-cell">
									{item.discount.map(d => `${d}%`).getOrElse("-")}
								</td>
								<td className={this.getAccountTdClassFromProps(item)}
									onClick={() => this.setState(
										{
											editedItemId: option(item.id),
											editedColumn: option(InvoiceItemColumn.Account)
										})}>
									{this.renderAccountCellContent(item, inv.partnerBrandId)}
								</td>
								<td className={this.getTaxRateTdClassFromProps(item)}
									onClick={() => this.setState(
										{
											editedItemId: option(item.id),
											editedColumn: option(InvoiceItemColumn.TaxRate)
										})}>
									{this.renderTaxRateCellContent(item, inv.vatRegistered)}
								</td>
								<td className="c-invoice-table_amount-cell">
									{item.amount ? CurrencyHelper.toCurrency(item.amount) : ""}
								</td>
								{this.props.editMode
								&& <td className="c-invoice-table_edit-cell" style={{borderRight: "none"}}>
									{item.type === InvoiceItemType.LABOUR_AND_PARTS &&
									<IconSmall icon={Icon.Edit}
											   variant={"gray"}
											   iconTooltipText={"Edit"}
											   onClick={this.props.onLabourAndPartsItemEditClick}/>}
									{item.type === InvoiceItemType.USER_DEFINED &&
									<IconSmall icon={Icon.X}
											   variant={"gray"}
											   iconTooltipText={"Delete"}
											   onClick={() => this.onDeleteItem(item.id)}/>}
								</td>}
							</tr>
						}
					)).orNull}
					</tbody>
				</DataGrid>
			</div>);
	}

	getDescThClassFromProps = () => classNames({
		"c-invoice-table_desc-cell": true,
		"without-extra-padding": this.props.editMode
	});

	getDescTdClassFromProps = (itemId: string) => classNames({
		"c-invoice-table_desc-cell": true,
		"without-extra-padding": this.props.editMode,
		"edit-mode": this.props.editMode && InvoiceItemColumn.Description === this.state.editedColumn.orNull &&
			itemId === this.state.editedItemId.orNull
	});

	getQtyTdClassFromProps = (item: InvoiceItem) => classNames({
		"c-invoice-table_qty-cell": true,
		"edit-mode": this.props.editMode && InvoiceItemColumn.Quantity === this.state.editedColumn.orNull &&
			item.id === this.state.editedItemId.orNull && item.type === InvoiceItemType.USER_DEFINED
	});

	getUnitPriceTdClassFromProps = (item: InvoiceItem) => classNames({
		"c-invoice-table_unit-price-cell": true,
		"edit-mode": this.props.editMode && InvoiceItemColumn.UnitPrice === this.state.editedColumn.orNull &&
			item.id === this.state.editedItemId.orNull && item.type === InvoiceItemType.USER_DEFINED
	});

	getAccountTdClassFromProps = (item: InvoiceItem) => classNames({
		"c-invoice-table_account-cell": true,
		"edit-mode": this.props.editMode && InvoiceItemColumn.Account === this.state.editedColumn.orNull &&
			item.id === this.state.editedItemId.orNull && item.type === InvoiceItemType.USER_DEFINED
	});

	getTaxRateTdClassFromProps = (item: InvoiceItem) => classNames({
		"c-invoice-table_tax-rate-cell": true,
		"edit-mode": this.props.editMode && InvoiceItemColumn.TaxRate === this.state.editedColumn.orNull &&
			item.id === this.state.editedItemId.orNull && item.type === InvoiceItemType.USER_DEFINED
	});

	onDeleteItem = (itemId: string) => {
		this.props.invoice.map(inv => {
			const updatedItems = inv.items.filter(item => item.id !== itemId);
			this.props.onChange(inv.withUpdatedItems(updatedItems));
		})
	};

	renderDescriptionCellContent = (item: InvoiceItem) => {
		const description = item.type === InvoiceItemType.LABOUR_AND_PARTS ?
			this.props.labourAndPartsItemDescription.getOrElse("")
			: item.description;

		if (this.props.editMode
			&& this.state.editedItemId.map(id => id === item.id).getOrElse(false)
			&& this.state.editedColumn.map(col => col === InvoiceItemColumn.Description).getOrElse(false)) {
			return (
				<Input type={"text"}
					   value={description}
					   onChange={(e) => this.onDescriptionChange(e.target.value, item)}/>
			);
		}
		return description;
	};

	renderQuantityCellContent = (item: InvoiceItem) => {
		if (this.props.editMode
			&& item.type !== InvoiceItemType.LABOUR_AND_PARTS
			&& this.state.editedItemId.map(id => id === item.id).getOrElse(false)
			&& this.state.editedColumn.map(col => col === InvoiceItemColumn.Quantity).getOrElse(false)) {
			return (
				<RangeNumberInput value={option(item.quantity)}
								  onChange={(value) => this.onQuantityChange(value, item.id)}
								  maxDecimalPlaces={0}
								  minValue={option(1)}/>
			);
		}
		return item.quantity;
	};

	renderUnitPriceCellContent = (item: InvoiceItem) => {
		if (this.props.editMode
			&& item.type !== InvoiceItemType.LABOUR_AND_PARTS
			&& this.state.editedItemId.map(id => id === item.id).getOrElse(false)
			&& this.state.editedColumn.map(col => col === InvoiceItemColumn.UnitPrice).getOrElse(false)) {
			const initialValue = this.state.tempUnitPrice ? this.state.tempUnitPrice : "";
			return (
				<CurrencyInput value={option(initialValue)}
							   onChange={(value) => this.onUnitPriceChange(value, item.id)}/>
			);
		}
		return item.unitPrice ? CurrencyHelper.toCurrency(item.unitPrice) : "";
	};

	renderAccountCellContent = (item: InvoiceItem, partnerBrandId: string) => {
		if (this.props.editMode
			&& item.type !== InvoiceItemType.LABOUR_AND_PARTS
			&& this.state.editedItemId.map(id => id === item.id).getOrElse(false)
			&& this.state.editedColumn.map(col => col === InvoiceItemColumn.Account).getOrElse(false)) {
			return <SelectAccount selectedId={item.accountCode}
								  onChanged={(account) => this.onAccountChange(account as Option<Account>, item.id)}
								  items={this.props.accounts}/>;
		}
		else {
			return item.accountCode.map(ac => {
				return <AccountNamePresenter partnerBrandId={partnerBrandId} accountCode={ac}/>
			}).orNull;
		}
	};

	renderTaxRateCellContent = (item: InvoiceItem, vatRegistered: boolean) => {
		const taxRates = [{value: 0}];
		if (vatRegistered && this.props.vat.isDefined) {
			taxRates.push({value: this.props.vat.get});
		}
		if (this.props.editMode
			&& item.type !== InvoiceItemType.LABOUR_AND_PARTS
			&& this.state.editedItemId.map(id => id === item.id).getOrElse(false)
			&& this.state.editedColumn.map(col => col === InvoiceItemColumn.TaxRate).getOrElse(false)) {
			return <SelectTaxRate items={option(taxRates)}
								  selectedId={item.taxRate !== null ? option(item.taxRate.toString()) : none}
								  onChanged={(taxRate) => this.onTaxRateChange(taxRate as Option<Tax>, item.id)}/>
		}
		else {
			return item.taxRate !== null ? `${item.taxRate}%` : "";
		}
	};

	addNewItem = () => {
		this.props.invoice.map(inv =>
			this.props.onChange(
				inv.withUpdatedItems(inv.items.concat(InvoiceItem.createEmpty(inv.vatRegistered, this.props.vat))))
		);
	};

	onDescriptionChange = (description: string, item: InvoiceItem) => {
		if (item.type === InvoiceItemType.LABOUR_AND_PARTS) {
			this.props.onLabourAndPartsItemDescriptionChange(description);
		}
		else {
			this.props.invoice.map(inv => {
				const updatedItems = inv.items.map(i => i.id === item.id ? i.copy({description}) : i);
				this.props.onChange(inv.withUpdatedItems(updatedItems));
			});
		}
	};

	onQuantityChange = (value: Option<number>, itemId: string) => {
		this.props.invoice.map(inv => {
			const quantity = value.orNull;
			const updatedItems = inv.items.map(i => i.id === itemId ? i.withNewQuantity(quantity) : i);
			this.props.onChange(inv.withUpdatedItems(updatedItems));
		});
	};

	onUnitPriceChange = (value: Option<string>, itemId: string) => {
		const updateInvoiceState = () => this.props.invoice.map(inv => {
			const unitPrice = Number(this.state.tempUnitPrice);
			const updatedItems = inv.items.map(i => i.id === itemId ? i.withNewUnitPrice(unitPrice) : i);
			this.props.onChange(inv.withUpdatedItems(updatedItems));
		});
		this.setState({tempUnitPrice: value.getOrElse("")}, updateInvoiceState);
	};

	onAccountChange = (account: Option<Account>, itemId: string) => {
		this.props.invoice.map(inv => {
			const updatedItems = inv.items.map(i => i.id === itemId ? i.copy({accountCode: account.map(a => a.code)}) : i);
			this.props.onChange(inv.withUpdatedItems(updatedItems));
		});
	};

	onTaxRateChange = (value: Option<Tax>, itemId: string) => {
		this.props.invoice.map(inv => {
			const taxRate = value.map(t => t.value).orNull;
			const updatedItems = inv.items.map(i => i.id === itemId ? i.copy({taxRate}) : i);
			this.props.onChange(inv.withUpdatedItems(updatedItems));
		});
	};
}
